import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BackendUserModel, RequestParamModel, ResponseModel, UserModel } from '../models';
import { PaginationModel } from '../models';
import { BaseService } from '../services';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class BackendUserService {

  constructor(private baseService: BaseService) { }
  // get all user list
  getBackEndUser(requestParamModel: RequestParamModel, skipLoader: boolean = false): any {
    return this.baseService.get('backend-user', requestParamModel, skipLoader)
      .pipe(map<any, any>(response => {
        const backedUserList = new PaginationModel<BackendUserModel>();
        const headers = response.headers;
        backedUserList.setHeaders(headers);
        backedUserList.data = response.body.data as BackendUserModel[];
        return backedUserList;
        }),
        catchError(err => throwError(() => err))
    );
  }

  delete(_id: any) {
    return this.baseService.delete('backend-user/' + _id)
      .pipe(map<any, number>(response => {
        return response;
      }),
      catchError(err => throwError(() => err))
  );
  }

  /**
   * Create (no id) or update (id) a console user, optionally with a new profile picture.
   *
   * A picture forces the request to be a multipart **POST**. PHP only populates `$_FILES`
   * for POST, so a PUT/PATCH body carries no file however it is encoded and the API would
   * read the request as "no new picture" — see the docblock on
   * `BackendUserController::actionUpdate`. `backend-user/:id` is routed to `update` for
   * POST as well as PUT/PATCH (`$apis['update']`), so the verb switch changes nothing else
   * about the call.
   *
   * Without a picture the request stays the JSON POST/PATCH it has always been. Sending
   * multipart unconditionally would be the simpler code and the wrong trade: FormData
   * stringifies every value, so the request would stop being able to express a type at all.
   */
  save(accountModel: any, profilePicture?: File | null): Observable<number> {
    const isEdit = accountModel.id !== undefined;
    const url = isEdit ? 'backend-user/' + accountModel.id : 'backend-user';

    const request = profilePicture
      ? this.baseService.uploadPost(url, BackendUserService.toFormData(accountModel, profilePicture))
      : (isEdit
        ? this.baseService.patch(url, accountModel)
        : this.baseService.post(url, accountModel));

    return request.pipe(
      map<any, number>(response => (response.data as BackendUserModel).id),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Clear a console user's profile picture — the S3 object and the column — so the listing
   * falls back to their initials.
   *
   * Only needed to get back to NO picture. REPLACING one does not need this call: `save()`
   * with a file already deletes the superseded object once the row points at its
   * replacement, so calling both would race the new picture against the old one's delete.
   *
   * `skipError`: a 404 here means "there was nothing to remove", which is the state the
   * caller asked for and not something to put a red toast in front of the user for. The
   * caller owns the whole outcome — including toasting anything that is a real failure.
   */
  deleteProfilePicture(_id: any): Observable<any> {
    return this.baseService.delete('backend-user/delete-profile-picture/' + _id, false, true)
      .pipe(map<any, any>((response: any) => response?.data),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * The model as multipart fields, next to the picture.
   *
   * Flat keys: the API loads these with `load(['BackendUser' => post()])`, which reads
   * `first_name`, not `BackendUser[first_name]`.
   *
   * `null`/`undefined` are SKIPPED rather than appended. FormData stringifies whatever it
   * is given, so an absent password would arrive as the literal string "undefined" and be
   * hashed as the user's new password — locking them out on an edit that never meant to
   * touch it. `id` is skipped too: it addresses the row through the URL, and the API does
   * not accept it as an assignable attribute.
   */
  private static toFormData(model: any, profilePicture: File): FormData {
    const formData = new FormData();
    Object.keys(model).forEach(key => {
      const value = model[key];
      if (key !== 'id' && value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });
    formData.append('profile_picture', profilePicture);
    return formData;
  }
  signUp(requestModel: any): Observable<UserModel> {
    return this.baseService.uploadPost('auth/register', requestModel)
      .pipe(map<any, UserModel>(response => {
        return response.data;
        }),
        catchError(err => throwError(() => err))
    );
  }
}
